import moment, { Moment } from 'moment-timezone'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import { LOCIZE_NAMESPACE } from 'main/common'
import Form, { FormGroup } from 'main/components/common/Form'
import { CANCEL_TOUR_MUTATION, CancelTour, CancelTourVariables, ITour } from 'main/gql/tour'
import { Button, spacing, Strong, TextArea, Title } from 'main/ui'
import { TFunction, useTranslation } from 'react-i18next'
import { i18nKeyPrefix } from '../../../i18n'
import {
  queryVariables,
  SCHEDULE_TOUR_MODAL_QUERY,
  ScheduleTourModal_school,
} from '../../common/gql'
import { InfoLabel, InfoText } from './Cancel.styles'

const DATE_TIME_FORMAT = 'dddd, MMMM D, YYYY, h:mma'
const PRE = `${i18nKeyPrefix}-SCHEDULE-MODAL-CANCEL`

type CancelTourMutationFn = MutationFunction<CancelTour, CancelTourVariables>

interface IProps {
  anchorMoment: Moment
  school: ScheduleTourModal_school
  tour: ITour
  onAbort: () => void
  onComplete: () => void
}

interface IParams {
  cancelationReason: string
}

interface IValidation {
  cancelationReason?: string
}

export default function Cancel(props: IProps) {
  const { anchorMoment, school, tour, onAbort, onComplete } = props
  const { director } = school
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const dateTime = moment(tour.beginsAt).tz(school.timezone).format(DATE_TIME_FORMAT)

  const [params, setParams] = React.useState<IParams>({ cancelationReason: '' })
  const [validationHints, setValidationHints] = React.useState<IValidation>({})

  return (
    <Mutation<CancelTour, CancelTourVariables>
      mutation={CANCEL_TOUR_MUTATION}
      onCompleted={onComplete}
      refetchQueries={[
        {
          query: SCHEDULE_TOUR_MODAL_QUERY,
          variables: queryVariables(school.id, anchorMoment),
        },
      ]}
    >
      {(mutate, { loading }) => (
        <Form onSubmit={() => handleSubmit(tour, params, mutate, setValidationHints, t)}>
          <div css={{ marginBottom: spacing.majorScale(2) }}>
            <Title kind="section">{t(`${PRE}-TITLE`)}</Title>
            <Title kind="section">{t(`${PRE}-TITLE-TWO`)}</Title>
          </div>

          <div css={{ marginBottom: spacing.majorScale(4) }}>
            <div>
              <InfoLabel>{school.name}</InfoLabel>
            </div>
            <div>
              <InfoLabel>{t(`${PRE}-DIRECTOR-LABEL`)}</InfoLabel>
              <InfoText>{director && director.name}</InfoText>
            </div>
            <div>
              <InfoLabel>{t(`${PRE}-TOUR-LABEL`)}</InfoLabel>
              <InfoText>{dateTime}</InfoText>
            </div>
          </div>

          <div css={{ marginBottom: spacing.majorScale(4) }}>
            <Strong>{t(`${PRE}-REASON`)}</Strong>
            <TextArea
              placeholder={t(`${PRE}-REASON-PLACEHOLDER`)}
              validationHint={validationHints.cancelationReason}
              value={params.cancelationReason}
              onChange={cancelationReason => setParams(prev => ({ ...prev, cancelationReason }))}
            />
          </div>

          <FormGroup>
            <Button
              disabled={loading}
              kind="outline"
              full
              onClick={event => {
                event.preventDefault()
                onAbort()
              }}
            >
              {t(`${PRE}-BACK`)}
            </Button>
            <Button disabled={loading} full>
              {loading ? t(`${PRE}-CANCELLING`) : t(`${PRE}-CONFIRM`)}
            </Button>
          </FormGroup>
        </Form>
      )}
    </Mutation>
  )
}

function handleSubmit(
  tour: ITour,
  params: IParams,
  mutate: CancelTourMutationFn,
  setValidationHints: (validationHints: IValidation) => void,
  t: TFunction,
) {
  const { cancelationReason } = params

  if (params.cancelationReason.trim() === '') {
    return setValidationHints({
      cancelationReason: t(`${PRE}-VALIDATION`),
    })
  }

  setValidationHints({})
  mutate({ variables: { id: tour.id, cancelationReason } })
}
